import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from "react";
import { Shell } from "@/components/shell/Shell";
import { LoginPage } from "@/routes/auth/LoginPage";
import { HomePage } from "@/routes/HomePage";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RequireGroup } from "@/components/auth/RequireGroup";
import { ConfirmHost } from "@/components/ConfirmDialog";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Route surfaces are code-split.
//
// Every router used to be a static import, so one bundle carried all of them —
// including @cloudflare/realtimekit-react and its UI kit, a full WebRTC stack
// pulled in by the meetings route and shipped to someone who only ever opens
// their inbox. Splitting here costs nothing structurally because each route
// already renders inside its own Boundary, which is the natural Suspense seam.

// Splitting introduced a failure the single-bundle build could not have: a tab
// that was open across a deploy still holds the previous index.html, so the
// first navigation to a not-yet-loaded route asks for a content-hashed chunk
// that no longer exists on the server. The import rejects and the route dies in
// its ErrorBoundary, which is what "meetings CRASHED" was.
//
// A stale document cannot be repaired in place — the fix is to fetch the
// current HTML, so we reload once. sessionStorage guards against a loop if the
// chunk is genuinely broken rather than merely stale, in which case we let the
// error through to the boundary and the user sees a real message.
const RELOAD_GUARD = "l7:chunk-reload";

// Mirrors React.lazy's own signature so the wrapped routes stay valid JSX.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyRoute<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await load();
      sessionStorage.removeItem(RELOAD_GUARD);
      return mod;
    } catch (err) {
      if (!sessionStorage.getItem(RELOAD_GUARD)) {
        sessionStorage.setItem(RELOAD_GUARD, String(Date.now()));
        window.location.reload();
        // Never resolves: the reload is already underway and rendering a
        // fallback here would only flash.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
const MailRouter = lazyRoute(() => import("@/routes/mail").then((m) => ({ default: m.MailRouter })));
const SupportRouter = lazyRoute(() => import("@/routes/support").then((m) => ({ default: m.SupportRouter })));
const FilesRouter = lazyRoute(() => import("@/routes/files").then((m) => ({ default: m.FilesRouter })));
const CalendarRouter = lazyRoute(() => import("@/routes/calendar").then((m) => ({ default: m.CalendarRouter })));
const ContactsRouter = lazyRoute(() => import("@/routes/contacts").then((m) => ({ default: m.ContactsRouter })));
const MeetingsRouter = lazyRoute(() => import("@/routes/meetings").then((m) => ({ default: m.MeetingsRouter })));
const SettingsRouter = lazyRoute(() => import("@/routes/settings").then((m) => ({ default: m.SettingsRouter })));
const PublicMeetingPage = lazyRoute(() => import("@/routes/meetings/PublicMeetingPage").then((m) => ({ default: m.PublicMeetingPage })));
const PublicBookingPage = lazyRoute(() => import("@/routes/book").then((m) => ({ default: m.PublicBookingPage })));
const PublicBookingCancelPage = lazyRoute(() => import("@/routes/book").then((m) => ({ default: m.PublicBookingCancelPage })));
const PublicRsvpPage = lazyRoute(() => import("@/routes/rsvp").then((m) => ({ default: m.PublicRsvpPage })));

// Per-route error boundary. Sits inside the Shell's <Outlet>, so a render throw
// in one surface shows the fallback in the content area while the shell + nav
// stay alive. Keyed on pathname so navigating to another route auto-recovers a
// crashed one without a hard reload.
// Shown while a split route chunk is in flight. Deliberately quiet: chunks are
// same-origin and usually land in a few hundred ms, so a spinner would flash.
function RouteFallback() {
  return <div className="p-6 text-fg-4 text-body-s">Loading…</div>;
}

function Boundary({ label, children }: { label: string; children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary label={label} resetKey={pathname}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export function App() {
  return (
    <>
    <Routes>
      {/* Public auth surfaces */}
      <Route path="/login/*" element={<LoginPage />} />

      {/* Public shareable meeting link — no auth required. Backed by
          /api/public/meetings/rooms/:id/join. Room must already exist. */}
      <Route path="/m/:roomId" element={<Boundary label="meeting"><PublicMeetingPage /></Boundary>} />

      {/* Public booking surface — no auth. External clients pick a time and
          book; /book/cancel/:token lets them self-cancel. Backed by /api/book/*. */}
      <Route path="/book/:slug" element={<Boundary label="book"><PublicBookingPage /></Boundary>} />
      <Route path="/book/cancel/:token" element={<Boundary label="book"><PublicBookingCancelPage /></Boundary>} />

      {/* Public RSVP surface — no auth. External attendees respond to a calendar
          invite via a per-attendee capability token. Backed by /api/rsvp/:token. */}
      <Route path="/rsvp/:token" element={<Boundary label="rsvp"><PublicRsvpPage /></Boundary>} />

      {/* Workspace, auth-gated */}
      <Route element={<RequireAuth><Shell /></RequireAuth>}>
        <Route index element={<Navigate to="/home" replace />} />
        <Route path="home" element={<Boundary label="home"><HomePage /></Boundary>} />
        <Route path="mail/*"     element={<Boundary label="mail"><MailRouter /></Boundary>} />
        <Route path="support/*"  element={
          // Non-agents get 404, not 403 — see plan §3.6.
          <RequireGroup group="support-agents" fallback="404">
            <Boundary label="support"><SupportRouter /></Boundary>
          </RequireGroup>
        } />
        <Route path="files/*"    element={<Boundary label="files"><FilesRouter /></Boundary>} />
        <Route path="calendar/*" element={<Boundary label="calendar"><CalendarRouter /></Boundary>} />
        <Route path="contacts/*" element={<Boundary label="contacts"><ContactsRouter /></Boundary>} />
        <Route path="meetings/*" element={<Boundary label="meetings"><MeetingsRouter /></Boundary>} />
        <Route path="settings/*" element={<Boundary label="settings"><SettingsRouter /></Boundary>} />
      </Route>

      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
    {/* Mounted once at the App root (not inside Shell) so the imperative
        confirmDialog() works on public routes too (e.g. /book/:slug, /m/:roomId). */}
    <ConfirmHost />
    </>
  );
}
